import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface Instructor {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  bio: string | null;
  active: boolean;
}

export interface InstructorInput {
  first_name: string;
  last_name: string;
  email?: string;
  bio?: string;
  active?: boolean;
}

export function useInstructors() {
  return useQuery({
    queryKey: ['instructors'],
    queryFn: async (): Promise<Instructor[]> => {
      const { data, error } = await supabase
        .from('instructors')
        .select('id, first_name, last_name, email, bio, active')
        .is('deleted_at', null)
        .order('last_name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateInstructor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InstructorInput): Promise<Instructor> => {
      const { data, error } = await supabase
        .from('instructors')
        .insert(input)
        .select('id, first_name, last_name, email, bio, active')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['instructors'] });
    },
  });
}

// Class ↔ instructor assignments.
export function useClassInstructors(classId: string | undefined) {
  return useQuery({
    enabled: !!classId,
    queryKey: ['class-instructors', classId],
    queryFn: async (): Promise<Instructor[]> => {
      const { data, error } = await supabase
        .from('class_instructors')
        .select('instructors(id, first_name, last_name, email, bio, active)')
        .eq('class_id', classId!);
      if (error) throw error;
      interface Row {
        instructors: Instructor | null;
      }
      return ((data ?? []) as Row[])
        .map((r) => r.instructors)
        .filter((i): i is Instructor => i !== null);
    },
  });
}

export function useAssignInstructor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { classId: string; instructorId: string }) => {
      const { error } = await supabase
        .from('class_instructors')
        .insert({ class_id: args.classId, instructor_id: args.instructorId });
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['class-instructors', args.classId] });
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
  });
}

export function useUnassignInstructor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { classId: string; instructorId: string }) => {
      const { error } = await supabase
        .from('class_instructors')
        .delete()
        .eq('class_id', args.classId)
        .eq('instructor_id', args.instructorId);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['class-instructors', args.classId] });
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
  });
}
